import { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { navLinks } from "@/mocks/bioconData";
import { SITE_IMAGES } from "@/lib/siteAssets";

interface NavChild {
  label: string;
  href?: string;
  children?: NavChild[];
}

interface NavLink {
  label: string;
  href: string;
  children?: NavChild[];
}

function isExternal(href: string): boolean {
  return href.startsWith("http://") || href.startsWith("https://");
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = location.pathname.startsWith("/admin");
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openNested, setOpenNested] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isAdmin) return;
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
        setOpenNested(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isAdmin]);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
    setOpenNested(null);
  }, [location.pathname]);

  if (isAdmin) {
    return null;
  }

  const handleNavClick = (href: string) => {
    if (!isExternal(href)) {
      navigate(href);
    }
  };

  const renderNavLink = (link: NavLink | NavChild, isMobile: boolean) => {
    const href = link.href || "";
    const external = isExternal(href);
    const baseClass = isMobile
      ? "block py-2 text-base font-medium text-[#2c4964] hover:text-[#1977cc]"
      : "px-3 py-2 text-base font-medium text-[#2c4964] hover:text-[#1977cc] transition-colors whitespace-nowrap";

    if (external) {
      return (
        <a
          href={href}
          rel="nofollow"
          className={baseClass}
          onClick={() => isMobile && setMobileOpen(false)}
        >
          {link.label}
        </a>
      );
    }

    return (
      <Link
        to={href}
        className={baseClass}
        onClick={() => isMobile && setMobileOpen(false)}
      >
        {link.label}
      </Link>
    );
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Main nav */}
      <div
        className={`transition-all duration-300 ${
          scrolled ? "bg-white shadow-sm border-b border-gray-100 py-4" : "bg-white/95 backdrop-blur-sm py-5"
        }`}
      >
        <div className="container mx-auto px-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-4">
            <img
              src={SITE_IMAGES.logo}
              alt="Biocon"
              className="h-14 w-auto"
            />
            <div className="flex flex-col">
              <span className="text-base font-semibold text-[#2c4964] leading-tight">biocon</span>
              <span className="text-sm text-gray-400 leading-tight">The Target Factory</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav ref={dropdownRef} className="hidden lg:flex items-center gap-1">
            {navLinks.map((link: NavLink) => (
              <div key={link.label} className="relative">
                {link.children ? (
                  <>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === link.label ? null : link.label)}
                      className="flex items-center gap-1 px-3 py-2 text-base font-medium text-[#2c4964] hover:text-[#1977cc] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {link.label}
                      <i className="ri-arrow-down-s-line text-xs" />
                    </button>
                    {openDropdown === link.label && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-100 shadow-lg rounded-lg min-w-[220px] py-2 z-50">
                        {link.children.map((child: NavChild) =>
                          child.children ? (
                            <div key={child.label} className="relative group/nested">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenNested(openNested === child.label ? null : child.label);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#1977cc] flex items-center justify-between cursor-pointer"
                              >
                                {child.label}
                                <i className="ri-arrow-right-s-line text-xs" />
                              </button>
                              {openNested === child.label && (
                                <div className="lg:absolute lg:left-full lg:top-0 lg:ml-1 static bg-white border border-gray-100 shadow-lg rounded-lg min-w-[200px] py-2 z-50">
                                  {child.children.map((sub: NavChild) => (
                                    <a
                                      key={sub.label}
                                      href={sub.href}
                                      rel="nofollow"
                                      className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#1977cc] transition-colors"
                                    >
                                      {sub.label}
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <a
                              key={child.label}
                              href={child.href}
                              rel="nofollow"
                              className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#1977cc] transition-colors"
                            >
                              {child.label}
                            </a>
                          )
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {isExternal(link.href) ? (
                      <a
                        href={link.href}
                        rel="nofollow"
                        className="px-3 py-2 text-base font-medium text-[#2c4964] hover:text-[#1977cc] transition-colors whitespace-nowrap"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="px-3 py-2 text-base font-medium text-[#2c4964] hover:text-[#1977cc] transition-colors whitespace-nowrap"
                      >
                        {link.label}
                      </Link>
                    )}
                  </>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2 text-[#2c4964]"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-2xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg max-h-[80vh] overflow-y-auto">
          <nav className="flex flex-col p-4 gap-1">
            {navLinks.map((link: NavLink) => (
              <div key={link.label}>
                {link.children ? (
                  <>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === link.label ? null : link.label)}
                      className="w-full text-left flex items-center justify-between py-2 text-base font-medium text-[#2c4964] cursor-pointer"
                    >
                      {link.label}
                      <i className={`ri-arrow-down-s-line text-xs transition-transform ${openDropdown === link.label ? "rotate-180" : ""}`} />
                    </button>
                    {openDropdown === link.label && (
                      <div className="pl-3 border-l-2 border-gray-100 ml-1 mt-1 mb-2">
                        {link.children.map((child: NavChild) =>
                          child.children ? (
                            <div key={child.label}>
                              <button
                                onClick={() => setOpenNested(openNested === child.label ? null : child.label)}
                                className="w-full text-left flex items-center justify-between py-1.5 text-sm text-gray-500 cursor-pointer"
                              >
                                {child.label}
                                <i className={`ri-arrow-down-s-line text-xs transition-transform ${openNested === child.label ? "rotate-180" : ""}`} />
                              </button>
                              {openNested === child.label && (
                                <div className="pl-3 border-l-2 border-gray-100 ml-1 mt-1 mb-1">
                                  {child.children.map((sub: NavChild) => (
                                    <a
                                      key={sub.label}
                                      href={sub.href}
                                      rel="nofollow"
                                      className="block py-1.5 text-sm text-gray-500 hover:text-[#1977cc]"
                                      onClick={() => setMobileOpen(false)}
                                    >
                                      {sub.label}
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <a
                              key={child.label}
                              href={child.href}
                              rel="nofollow"
                              className="block py-1.5 text-sm text-gray-500 hover:text-[#1977cc]"
                              onClick={() => setMobileOpen(false)}
                            >
                              {child.label}
                            </a>
                          )
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {isExternal(link.href) ? (
                      <a
                        href={link.href}
                        rel="nofollow"
                        className="block py-2 text-base font-medium text-[#2c4964] hover:text-[#1977cc]"
                        onClick={() => setMobileOpen(false)}
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="block py-2 text-base font-medium text-[#2c4964] hover:text-[#1977cc]"
                        onClick={() => setMobileOpen(false)}
                      >
                        {link.label}
                      </Link>
                    )}
                  </>
                )}
              </div>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
